import React, { useEffect, useState } from 'react';
import { useSearchStatementContext } from './search-statement-context';
import DisplayDataStructure from '../../../lib/display-data-structure';
import { popupNotification } from '../../../popup-notification';
import { createQueryErrorMessage } from '../../../services/local-relational-database/utils';
import { ISelectQueryResultV2 } from '../../../services/local-relational-database';
import Table from '../../../lib/table/Table';
import { ITableRowDataPoint, TableSortType } from '../../../lib/table/types';
import {
	IValueResolutionContext,
	searchStatementDefs
} from '@elyx-code/project-logic-tree';
import { fromColumnRefToTableColumnType } from '../../../services/database';
import { getLiteralValueNameFromDataType } from '../../../services/execution/utils';
import {
	createStateFromType,
	ILiteralValueTransfer,
	LiteralValueState,
	checkIsNullable
} from '@elyx-code/project-logic-tree';
import { resolveValue } from '../../../services/execution/value-resolution';
import { Logger } from '@elyx-code/common-ts-utils';
import { DynamicValueTypes } from '../../../services/editor/value-store';

const { SortStatementDirection } = searchStatementDefs;

type SortStatementDirection = searchStatementDefs.SortStatementDirection;

const NUMBER_OF_ROWS = 5;

const ResultsSection = ({}: {}) => {
	const {
		asList,
		sqlAST,
		resolveDatabaseClient,
		searchStatementState,
		entity,
		project,
		isNested
	} = useSearchStatementContext();

	const [result, setResult] = useState<ISelectQueryResultV2 | null>(null);
	const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const removeValue = () => {
		if (isNested) {
			return;
		}

		const outputMap = entity.outputs[0];

		if (!outputMap) {
			return;
		}

		project.persistedExecutionResults.removeValue(outputMap.id);
	};

	// Writes value to the local testing values store
	const writeValue = (
		newValue:
			| {
					[columnName: string]:
						| string
						| null
						| number
						| boolean
						| Date;
			  }[]
			| {
					[columnName: string]:
						| string
						| null
						| number
						| boolean
						| Date;
			  }
			| null
	) => {
		if (isNested) {
			return;
		}

		const outputMap = entity.outputs[0];

		if (!outputMap) {
			return;
		}

		let literalValueEntity = project.persistedExecutionResults.readValue(
			outputMap.id
		);

		if (!literalValueEntity) {
			const literalValueTransfer: ILiteralValueTransfer =
				LiteralValueState.new(null);
			literalValueTransfer.name = getLiteralValueNameFromDataType(
				outputMap.getDataType(null)
			);
			literalValueTransfer.standaloneParent = outputMap.toReference();

			const newInstance = createStateFromType(
				literalValueTransfer,
				project.logic
			) as LiteralValueState;

			newInstance.setStandaloneParent(outputMap, null);
			newInstance.initChildren(null);

			literalValueEntity = newInstance;
		}

		literalValueEntity.metaSync(
			{
				value: newValue
			},
			null
		);

		project.persistedExecutionResults.writeValue(
			outputMap,
			literalValueEntity
		);
	};

	// Compiles and runs the latest query as raw SQL
	const queryDatabase = async () => {
		Logger.log('Querying database...: ', searchStatementState);
		try {
			setIsLoadingResults(true);

			const dbClient = resolveDatabaseClient();

			Logger.log('dbClient:', dbClient);

			const resolvedValues = (entity?.inputs || []).map((input) =>
				resolveValue(input,
        //   {
				// 	...project,
				// 	getValueTypePreference() {
				// 		return DynamicValueTypes.Testing;
				// 	}
				// } as unknown as IValueResolutionContext
        project,
        false
      )
			);

			console.log(
				'Resolved value: ',
				resolvedValues.map(
					(dv) =>
						`${dv.type}: ${JSON.stringify(dv.value?.value, null, 2)}`
				)
			);

			const newResult: ISelectQueryResultV2 =
				(await dbClient?.runStatement(
					searchStatementState,
					resolvedValues
				)) as ISelectQueryResultV2;

			if (!!newResult) {
				const newValue = asList
					? newResult.rows
					: newResult.rows[0] || null;
				writeValue(newValue);
			} else {
				removeValue();
			}

			Logger.log('Query result:', newResult);
			setResult(newResult);
		} catch (error) {
			Logger.error(error);
			const message = createQueryErrorMessage(error, []);

			popupNotification({
				text: message,
				color: 'error'
			});
		}

		setIsLoadingResults(false);
	};

	useEffect(() => {
		// Query the database and select the first row as a single result
		queryDatabase();
	}, [sqlAST]);

	return (
		<section
			style={{
				display: 'flex',
				flexDirection: 'column',
				paddingTop: 20,
				width: '100%',
				minWidth: 0
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 16,
					marginLeft: 80
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 4
					}}
				>
					<h2
						style={{
							margin: 0,
							fontSize: 16,
							fontWeight: 400
						}}
					>
						Results
					</h2>
					<p
						style={{
							margin: 0,
							fontSize: 10,
							color: 'rgba(0,0,0,0.54)'
						}}
					>
						The final results, found by your search statement
					</p>
				</div>
			</div>

			<br />

			{asList ? (
				<div style={{ marginLeft: 40, marginRight: 40, minWidth: 0 }}>
					<Table
						project={project}
						tableKey="search-results-table-key"
						totalItems={result?.total || 0}
						searches={[]}
						sorts={(searchStatementState?.validSortings || []).map(
							(sort) => ({
								column: sort.column.as,
								direction:
									sort.direction ===
									SortStatementDirection.Ascending
										? TableSortType.ASC
										: TableSortType.DESC
							})
						)}
						emptyMessage={
							searchStatementState.isValid
								? 'No results found'
								: "The current state of the search statement isn't valid. Please fix the errors shown and try again."
						}
						footerHelperText="Modifying the database directly bypasses the code. No events or actions are triggered."
						loading={isLoadingResults}
						columns={(result?.columns || []).map((column) => ({
							value: column.as,
							label: column.as,
							sortable: false,
							// TODO: Fixed this type error
							sort: null as any,
							searchable: false,
							hidden: false,
							editable: false,
							type: fromColumnRefToTableColumnType(column),
							primaryKey:
								searchStatementState?.primaryKey?.id ===
								column.id,
							foreignKey: null as any,
							nullable: (column as any).property
								? checkIsNullable(
										(column as any).property.dataType
									)
								: true,
							size: 'lg'
						}))}
						hideCreate={!result?.tableName}
						hideDelete={!result?.tableName}
						hideSelection={!result?.tableName}
						hideRowActions={!result?.tableName}
						onSearch={() => {}}
						onSearchesUpdate={() => {}}
						onSelection={() => {}}
						rows={(result?.rows || []).map((row) => ({
							onSelect() {},
							data: (result?.columns || []).map((column) => ({
								value:
									row[column.as] !== null &&
									row[column.as] !== undefined
										? String(row[column.as])
										: null
							})) as ITableRowDataPoint[],
							menuOptions: [] as any[]
						}))}
						onCreate={() => {}}
						onUpdate={() => {}}
						onDelete={() => {}}
						onDeleteMany={() => {}}
						onDeleteAll={() => {}}
						onSort={() => {}}
						onSortsUpdate={() => {}}
						pagination={{
							onChange: (page: number) => {
								setCurrentPage(page);
							},
							total:
								Math.ceil(
									(result?.total || 0) / NUMBER_OF_ROWS
								) || 1,
							page: currentPage
						}}
					/>
				</div>
			) : isLoadingResults ? (
				<p className="search-statement__results-section__single-item-empty-placeholder-text">
					Loading...
				</p>
			) : !!result?.rows[0] ? (
				<span
					style={{
						marginLeft: 80
					}}
				>
					<DisplayDataStructure value={result?.rows[0]} />
				</span>
			) : (
				<p className="search-statement__results-section__single-item-empty-placeholder-text">
					No entry found
				</p>
			)}
		</section>
	);
};

export default ResultsSection;
